import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Chart, ChartConfiguration, Plugin, Tooltip } from 'chart.js';
import { finalize, Subscription } from 'rxjs';
import { ThemeService } from 'src/app/core/services/theme.service';
import { DateRangeUtils } from 'src/app/shared/components/linear-datetime-range-picker/models/date-range-utils';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import {
  SmilebackReview,
  SmilebackReviewsPage,
  SmilebackSummary,
  TicketAnalysisFilters,
} from '../../../models/ticket-analysis.models';
import { TicketAnalysisService } from '../../../services/ticket-analysis.service';
import { ViewPersistenceService } from 'src/app/features/lifecycle-command/services/view-persistence.service';
import {
  applyFilterDropdowns,
  createTicketAnalysisFilterConfigMap,
  createTicketAnalysisFiltersForm,
  smilebackDefaultDateFrom,
  extractBaseFilters,
  toDateString,
} from '../ticket-analysis-filters.utils';
import { FiltersPanelLayoutConfig } from 'src/app/features/reactive-forms-ui/models/filters-panel-layout-config';
import {
  BAR_CATEGORY_PERCENTAGE,
  BAR_PERCENTAGE,
  SMILEBACK_CSAT_LINE_COLOR,
  SMILEBACK_CSAT_LINE_COLOR_DARK,
  VARIANCE_YEAR_LABEL_COLOR,
  withChartHoverAlpha,
} from '../ticket-analysis-chart.constants';

const SMILEBACK_POSITIVE_COLOR = '#00c100';
const SMILEBACK_NEUTRAL_COLOR = '#d8b706';
const SMILEBACK_NEGATIVE_COLOR = '#bf360c';

@Component({
  selector: 'ticket-analysis-smileback',
  templateUrl: './ticket-analysis-smileback.component.html',
  styleUrls: ['./ticket-analysis-smileback.component.scss'],
  standalone: false,
})
export class TicketAnalysisSmilebackComponent implements OnInit, OnDestroy {
  loading = false;
  loadingReviews = false;
  error = '';
  summary: SmilebackSummary | null = null;
  reviewsPage: SmilebackReviewsPage | null = null;

  currentPage = 1;
  pageSize = 25;
  sortBy = 'dateRated';
  sortDir: 'asc' | 'desc' = 'desc';
  readonly displayedColumns = ['ticketId', 'dateRated', 'closedDate', 'rating', 'contactName', 'comment'];

  csatBarChartData: any = { labels: [], datasets: [] };
  csatBarOptions: any = {};
  gaugeChartData: ChartConfiguration<'doughnut'>['data'] = { datasets: [] };
  gaugeOptions: ChartConfiguration<'doughnut'>['options'] = {};

  private labelColor = '#555555';
  private labelBg = 'rgba(255,255,255,0.9)';
  private csatLineColor = SMILEBACK_CSAT_LINE_COLOR;
  private yearGroups: { year: string; start: number; end: number }[] = [];
  private loadSub: Subscription | null = null;
  private loadReviewsSub: Subscription | null = null;

  readonly csatChartPlugins: Plugin[] = [
    {
      id: 'csatBarTopLabels',
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx, data } = chart;
        const nMonths = data.labels?.length ?? 0;

        for (let j = 0; j < nMonths; j++) {
          const total = [0, 1, 2].reduce((sum, di) => sum + ((data.datasets[di]?.data[j] as number) ?? 0), 0);
          if (!total) continue;
          let topEl: any = null;
          for (let di = 2; di >= 0; di--) {
            const val = data.datasets[di]?.data[j] as number;
            if (val > 0) {
              topEl = chart.getDatasetMeta(di).data[j];
              break;
            }
          }
          if (!topEl) continue;
          const { x, y } = topEl.getProps(['x', 'y'], true);
          let labelY = y - 4;

          const csatMeta = chart.getDatasetMeta(3);
          const csatPoint = csatMeta?.visible ? (csatMeta.data[j] as any) : null;
          if (csatPoint) {
            const { y: csatY } = csatPoint.getProps(['y'], true);
            const pointRadius = (csatPoint.options?.radius as number) ?? 4;
            const minGap = pointRadius + 12;
            if (Math.abs(labelY - csatY) < minGap) {
              labelY = Math.min(labelY, csatY - minGap);
            }
          }

          ctx.save();
          ctx.font = '400 12px Inter, sans-serif';
          ctx.fillStyle = this.labelColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(String(total), x, labelY);
          ctx.restore();
        }

        const csatMeta = chart.getDatasetMeta(3);
        for (let di = 0; di <= 2; di++) {
          const meta = chart.getDatasetMeta(di);
          if (!meta.visible) continue;
          for (let j = 0; j < nMonths; j++) {
            const val = (data.datasets[di]?.data[j] as number) ?? 0;
            if (!val) continue;
            const bar = meta.data[j] as any;
            const { x, y, base } = bar.getProps(['x', 'y', 'base'], true);
            const segHeight = Math.abs(base - y);
            if (segHeight < 14) continue;
            let labelY = y + segHeight / 2;

            const csatPoint = csatMeta?.visible ? (csatMeta.data[j] as any) : null;
            if (csatPoint) {
              const { y: csatY } = csatPoint.getProps(['y'], true);
              const pointRadius = (csatPoint.options?.radius as number) ?? 4;
              const minGap = pointRadius + 10;
              if (Math.abs(labelY - csatY) < minGap) {
                labelY = labelY < csatY ? csatY - minGap : csatY + minGap;
              }
            }

            ctx.save();
            ctx.font = '600 11px Inter, sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(val), x, labelY);
            ctx.restore();
          }
        }
      },
    },
    {
      id: 'csatYearGroupLabels',
      afterDraw: (chart: Chart) => {
        if (!this.yearGroups.length) return;
        const { ctx, scales, chartArea } = chart;
        const xAxis = scales['x'];
        if (!xAxis || !chartArea) return;
        ctx.save();
        ctx.font = 'italic 600 14px Inter, sans-serif';
        ctx.fillStyle = VARIANCE_YEAR_LABEL_COLOR;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        for (const { year, start, end } of this.yearGroups) {
          const cx = (xAxis.getPixelForValue(start) + xAxis.getPixelForValue(end)) / 2;
          ctx.fillText(year, cx, chartArea.bottom + 36);
        }
        ctx.restore();
        for (let i = 1; i < this.yearGroups.length; i++) {
          const sepX =
            (xAxis.getPixelForValue(this.yearGroups[i].start - 1) + xAxis.getPixelForValue(this.yearGroups[i].start)) /
            2;
          ctx.save();
          ctx.setLineDash([4, 4]);
          ctx.strokeStyle = this.labelColor + '55';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(sepX, chartArea.top - 30);
          ctx.lineTo(sepX, chartArea.bottom + 30);
          ctx.stroke();
          ctx.restore();
        }
      },
    },
  ];

  readonly gaugeChartPlugins: Plugin[] = [
    {
      id: 'ticketAnalysisSmilebackGaugeCenterText',
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx, chartArea } = chart;
        if (!chartArea || !this.summary || chart.data.datasets.length !== 1) return;
        const cx = (chartArea.left + chartArea.right) / 2;
        const cy = chartArea.bottom;
        ctx.save();
        ctx.font = 'bold 20px Inter, sans-serif';
        ctx.fillStyle = '#21b3ff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`${this.summary.netCsatScore}%`, cx, cy - 4);
        ctx.font = '400 10px Inter, sans-serif';
        ctx.fillStyle = this.labelColor;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText('0%', chartArea.left, cy);
        ctx.textAlign = 'right';
        ctx.fillText('100%', chartArea.right, cy);
        ctx.restore();
      },
    },
  ];

  filtersForm = createTicketAnalysisFiltersForm(smilebackDefaultDateFrom());
  valuesMap: any = this.filtersForm.value;
  filtersConfigMap: Map<string, IFormControlConfig> = createTicketAnalysisFilterConfigMap(
    smilebackDefaultDateFrom(),
    'lastYear',
  );
  readonly filtersLayoutConfig = new FiltersPanelLayoutConfig({
    elementIds: ['dateRange', 'site', 'board', 'status', 'source', 'contactName', 'rating'],
    title: 'Filters',
  });

  private readonly STORAGE_KEY = 'ticket_analysis_smileback';

  constructor(
    private readonly ticketAnalysisService: TicketAnalysisService,
    private readonly themeService: ThemeService,
    private readonly destroyRef: DestroyRef,
    private readonly viewPersistenceService: ViewPersistenceService,
  ) {}

  ngOnDestroy(): void {
    Chart.unregister(...this.csatChartPlugins, ...this.gaugeChartPlugins);
  }

  ngOnInit(): void {
    if (!(Tooltip.positioners as any)['csatPoint']) {
      (Tooltip.positioners as any)['csatPoint'] = (_elements: any[]) => {
        const csatEl = _elements.find((el: any) => el.datasetIndex === 3);
        if (csatEl) return { x: csatEl.element.x, y: csatEl.element.y };
        const barEl = _elements.find((el: any) => el.datasetIndex < 3);
        return barEl ? { x: barEl.element.x, y: barEl.element.y } : false;
      };
    }
    this.buildChartOptions();
    this.themeService.themeSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildChartOptions();
      if (this.summary) {
        this.buildCharts(this.summary);
        this.buildGaugeChart(this.summary);
      }
    });

    this.restoreFilters();
    this.loadDropdowns();
    this.load();

    this.ticketAnalysisService.exportRequested$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.ticketAnalysisService.startExport(this.buildFilters(), { view: 'smileback' });
    });
  }

  updateValuesMap(valuesMap: any): void {
    this.valuesMap = valuesMap;
  }

  applyFilters(): void {
    this.saveFilters();
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({
      dateRange: new DateRangeUtils().buildLastYearDateRange(),
      site: null,
      board: null,
      contactName: null,
      status: null,
      source: null,
      rating: null,
    });
    this.valuesMap = this.filtersForm.value;
    this.viewPersistenceService.clearFilters(this.STORAGE_KEY);
    this.load();
  }

  get noData(): boolean {
    return !!this.summary && this.summary.totalReviews === 0;
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadReviews();
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active || 'dateRated';
    this.sortDir = (sort.direction as 'asc' | 'desc') || 'desc';
    this.currentPage = 1;
    this.loadReviews();
  }

  ratingLabel(rating: SmilebackReview['rating']): string {
    const map: Record<string, string> = { positive: 'Positive', neutral: 'Neutral', negative: 'Negative' };
    return map[rating] ?? rating;
  }

  ratingClass(rating: SmilebackReview['rating']): string {
    const map: Record<string, string> = {
      positive: 'sb__rating--positive',
      neutral: 'sb__rating--neutral',
      negative: 'sb__rating--negative',
    };
    return map[rating] ?? '';
  }

  private load(): void {
    this.loadSub?.unsubscribe();
    this.currentPage = 1;
    this.loading = true;
    this.error = '';
    this.loadSub = this.ticketAnalysisService
      .getSmileback(this.buildFilters())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (data) => {
          this.summary = data;
          this.buildCharts(data);
          this.buildGaugeChart(data);
        },
        error: (err: HttpErrorResponse) => {
          this.summary = null;
          this.error = this.resolveSmilebackErrorMessage(err);
        },
      });
    this.loadReviews();
  }

  private resolveSmilebackErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 503 && err.error?.error?.code === 'SMILEBACK_NOT_CONFIGURED') {
      return 'SmileBack is not configured for this environment.';
    }
    return 'Failed to load SmileBack data.';
  }

  private loadReviews(): void {
    this.loadReviewsSub?.unsubscribe();
    this.loadingReviews = true;
    this.loadReviewsSub = this.ticketAnalysisService
      .getSmilebackReviews(this.buildFilters(), this.currentPage, this.pageSize, this.sortBy, this.sortDir)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loadingReviews = false)),
      )
      .subscribe({
        next: (data) => (this.reviewsPage = data),
        error: () => {
          this.reviewsPage = null;
          this.error = 'Failed to load SmileBack reviews.';
        },
      });
  }

  private loadDropdowns(): void {
    this.ticketAnalysisService.getFilters().subscribe({
      next: (response) => {
        this.filtersConfigMap = applyFilterDropdowns(response, this.filtersConfigMap);
      },
    });
  }

  private buildFilters(): TicketAnalysisFilters {
    const filters = extractBaseFilters(this.valuesMap, this.filtersConfigMap);
    if (!this.valuesMap?.dateRange?.start) {
      filters.dateFrom = toDateString(smilebackDefaultDateFrom());
    }
    return filters;
  }

  private buildCharts(data: SmilebackSummary): void {
    const yearGroupMap = new Map<string, { start: number; end: number }>();
    const labels = data.byMonth.map(({ month }, index) => {
      const [year, m] = month.split('-');
      if (!yearGroupMap.has(year!)) {
        yearGroupMap.set(year!, { start: index, end: index });
      } else {
        yearGroupMap.get(year!)!.end = index;
      }
      return new Date(+year!, +m! - 1).toLocaleString('en-US', { month: 'short' }).toUpperCase();
    });
    this.yearGroups = Array.from(yearGroupMap.entries()).map(([year, range]) => ({ year, ...range }));

    const barDefaults = {
      barPercentage: BAR_PERCENTAGE,
      categoryPercentage: BAR_CATEGORY_PERCENTAGE,
      stack: 'reviews',
    };

    this.csatBarChartData = {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Positive',
          data: data.byMonth.map((m) => m.positive),
          backgroundColor: SMILEBACK_POSITIVE_COLOR,
          hoverBackgroundColor: withChartHoverAlpha(SMILEBACK_POSITIVE_COLOR),
          ...barDefaults,
          order: 2,
        },
        {
          type: 'bar',
          label: 'Neutral',
          data: data.byMonth.map((m) => m.neutral),
          backgroundColor: SMILEBACK_NEUTRAL_COLOR,
          hoverBackgroundColor: withChartHoverAlpha(SMILEBACK_NEUTRAL_COLOR),
          ...barDefaults,
          order: 2,
        },
        {
          type: 'bar',
          label: 'Negative',
          data: data.byMonth.map((m) => m.negative),
          backgroundColor: SMILEBACK_NEGATIVE_COLOR,
          hoverBackgroundColor: withChartHoverAlpha(SMILEBACK_NEGATIVE_COLOR),
          ...barDefaults,
          order: 2,
        },
        {
          type: 'line',
          label: 'CSAT %',
          data: data.byMonth.map((m) => m.csatPct),
          borderColor: this.csatLineColor,
          backgroundColor: 'transparent',
          pointBackgroundColor: this.csatLineColor,
          pointRadius: 4,
          borderWidth: 2,
          tension: 0.3,
          yAxisID: 'y2',
          order: 1,
        },
      ],
    };
  }

  private buildGaugeChart(data: SmilebackSummary): void {
    const score = data.netCsatScore ?? 0;
    this.gaugeChartData = {
      datasets: [
        {
          data: [score, 100 - score],
          backgroundColor: ['#21b3ff', '#e0e0e0'],
          borderWidth: 0,
        },
      ],
    };
  }

  private buildChartOptions(): void {
    const isDark = this.themeService.isInDarkMode();
    this.labelColor = isDark ? '#F1F1F1' : '#555555';
    this.labelBg = isDark ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)';
    this.csatLineColor = isDark ? SMILEBACK_CSAT_LINE_COLOR_DARK : SMILEBACK_CSAT_LINE_COLOR;
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

    this.csatBarOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 28, bottom: 36 } },
      interaction: { mode: 'point' as const, intersect: true },
      plugins: {
        legend: { display: false },
        tooltip: {
          position: 'csatPoint' as any,
          filter: (item: any) => item.datasetIndex <= 3,
          callbacks: {
            title: () => '',
            label: (context: any) => {
              if (context.datasetIndex === 3) return ` ${context.parsed.y}%`;
              const labels = [' Positive', ' Neutral', ' Negative'];
              return `${labels[context.datasetIndex]}: ${context.parsed.y}`;
            },
            labelColor: (context: any) => {
              const colors = [
                SMILEBACK_POSITIVE_COLOR,
                SMILEBACK_NEUTRAL_COLOR,
                SMILEBACK_NEGATIVE_COLOR,
                this.csatLineColor,
              ];
              const c = colors[context.datasetIndex] ?? '#ffffff';
              return { borderColor: c, backgroundColor: c, borderWidth: 0, borderRadius: 3 };
            },
          },
          displayColors: true,
          backgroundColor: 'rgba(20,20,20,0.85)',
          bodyColor: '#ffffff',
          cornerRadius: 10,
          padding: { x: 10, y: 6 },
          bodyFont: { size: 12, weight: '600' as const },
        },
      },
      scales: {
        x: { stacked: true, ticks: { color: this.labelColor }, grid: { color: gridColor } },
        y: {
          stacked: true,
          ticks: { color: this.labelColor, precision: 0 },
          grid: { color: gridColor },
          beginAtZero: true,
          suggestedMax: 100,
        },
        y2: {
          type: 'linear' as const,
          position: 'right' as const,
          min: 0,
          max: 100,
          display: false,
          grid: { display: false },
        },
      },
    };

    this.gaugeOptions = {
      responsive: true,
      maintainAspectRatio: false,
      circumference: 180,
      rotation: -90,
      cutout: '65%',
      plugins: {
        legend: { display: false },
        tooltip: {
          filter: (item: any) => item.dataIndex === 0,
          displayColors: false,
          callbacks: {
            title: () => '',
            label: (context: any) => `${context.parsed}%`,
          },
          backgroundColor: 'rgba(20,20,20,0.85)',
          bodyColor: '#ffffff',
          cornerRadius: 6,
          padding: { top: 4, bottom: 4, left: 6, right: 6 },
          bodyFont: { size: 10, weight: '600' as const },
        },
      },
    };
  }

  private saveFilters(): void {
    const dateRange = this.valuesMap?.dateRange as { start: Date; end: Date } | null;
    this.viewPersistenceService.saveFilters(this.STORAGE_KEY, {
      ...this.valuesMap,
      dateRange: dateRange
        ? { start: dateRange.start?.toISOString?.() ?? null, end: dateRange.end?.toISOString?.() ?? null }
        : null,
    });
  }

  private restoreFilters(): void {
    const saved = this.viewPersistenceService.loadFilters(this.STORAGE_KEY);
    if (!saved) return;
    const rawRange = saved['dateRange'] as { start: string; end: string } | null;
    const start = rawRange?.start ? new Date(rawRange.start) : smilebackDefaultDateFrom();
    const end = rawRange?.end ? new Date(rawRange.end) : new Date();
    const asTree = (v: unknown) =>
      v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : null;
    const restored = {
      dateRange: { start, end },
      contactName: asTree(saved['contactName']),
      board: asTree(saved['board']),
      site: asTree(saved['site']),
      status: asTree(saved['status']),
      source: asTree(saved['source']),
      rating: asTree(saved['rating']),
    };
    this.filtersForm.patchValue(restored);
    this.valuesMap = restored;
  }
}
